import Can from "../../casl/Can";
import DataTable from "../shared/table";
import Header from "../shared/header";
import React, { FunctionComponent, useState } from "react";
import { Box } from "@mui/material";
import { columns } from "./assigned-tasks.columns";
import { Outlet } from "react-router-dom";
import { transformFilterItemToFilterObject, userRoleKey } from "../../utils/helper";
import { useFetchAssignedTasks } from "../../api/tasks";
import {
  GridFilterItem,
  GridPaginationModel,
  GridSortItem,
} from "@mui/x-data-grid";
import useFiltersStore from "../../stores/store-filters";
import { RolesTypeEnum } from "../../models/roles/roles.model";
import UnpaidClientDataTable from "../shared/unpaid-client-data-table";
import useSearchStore from "../../stores/store-query-filters"; 
import SearchBar from "../shared/search-bar";

interface AssignedTasksListProps {}

/**
 * TasksList is a React component that displays a paginated, sortable and filterable list of assigned tasks.
 * It integrates with a data table component and handles server-side operations.
 * 
 * @component
 * @param {Object} props - Component props (currently empty interface)
 * @returns {JSX.Element} A component that renders a list of assigned tasks with filtering and pagination
 * 
 * @features
 * - Server-side pagination
 * - Server-side sorting
 * - Server-side filtering
 * - Permission-based rendering via Can component
 * - Responsive layout
 * - Integration with filter store
 * - Custom header
 */
const TasksList: FunctionComponent<AssignedTasksListProps> = (
  props: AssignedTasksListProps
) => {
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  // Filter
  const filterKey = "activities";
  const filter = useFiltersStore((state) => state.getFilterObject(filterKey));
  // Sort
  const [sort, setSort] = useState({});

    const searchKey = 'activities';
  const searchQuery = useSearchStore((state) => state.getSearch(searchKey));

  const {
    data: assignedTasksData,
    isLoading,
    isError,

  } = useFetchAssignedTasks(searchQuery.trim(), currentPage, itemsPerPage, undefined, sort);
  const tasks = assignedTasksData?.data;
  const totalItems = assignedTasksData?.pager?.total;

  const parentPath = `activity/sub-tasks`;

  const handlePageChange = (newPage: GridPaginationModel) => {
    // Fetch new data from the server based on the new page
    setCurrentPage(newPage.page + 1);
    setItemsPerPage(newPage.pageSize);
  };

  const handleSortModelChange = (sortItem: GridSortItem) => {
    setSort(sortItem);
  };

  return (
    <>
      {!isError && (
        <Box
          sx={{
            marginTop: "60px",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Header title="Aufgaben" />
          </Box>
          {userRoleKey === RolesTypeEnum.ClientUnpaid ?
            <Box marginTop="40px" display="flex">
              <UnpaidClientDataTable
                columns={columns}
                minHeight={500}
                message="Mit einer Vollversion könnten Sie Aufgaben erstellen und alle nötigen Information Ihren Anlagen zuordnen"
              />
            </Box> : <Can I="view" a="Tasks">
                            <Box marginTop="20px" display="flex" flexDirection="column" height="70%">

            <Box mb={1}>
              <SearchBar searchKey={searchKey} onSearch={() => setCurrentPage(1)} />
            </Box>
                <DataTable
                  filterKey={filterKey}
                  parentPath={parentPath}
                  data={tasks ?? []}
                  fieldsNames={columns}
                  minHeight={500}
                  noDataMessage="Keine zugewiesene Aufgabe für Sie"
                  currentPage={currentPage - 1}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  ssrFilter={true}
                  isLoading={isLoading}
                  onSortChange={handleSortModelChange}
                  ssrSort={true}
                />
              </Box>
            </Can>}

          <Outlet />
        </Box>
      )}
    </>
  );
};

export default TasksList;
